import { z } from 'zod';
import { MAX_REPORT_RANGE_DAYS, boundedDateRange } from './payroll.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';

/**
 * THE QUERY EVERY REPORT ENDPOINT TAKES — the range, and the filter bar.
 *
 * Lifted out of `payroll.schema.ts`, where it had grown to a fifth of the file
 * while having nothing to do with payroll: the eight report routes wire it with
 * `validate(ReportRangeSchema, 'query')` and the workspace's filter bar is
 * exactly this object's optional half.
 *
 * IT IS ONE DIRECTION ONLY — this file imports the refinement from
 * `payroll.schema.ts` and that file must never import back. See
 * `importCycles.test.ts` for what a two-way edge between schema modules did to
 * the API's boot.
 *
 * READ THE FIELD NOTES BEFORE ADDING ONE. Five parameters have now been lost to
 * the same trap, because `validate` overwrites `req.query` with the Zod-parsed
 * object and Zod strips keys this schema does not declare — so a filter absent
 * from here renders, writes the URL, draws its chip, stamps the export URL, and
 * the server never sees it. Each note below is one of those.
 */

export const ReportRangeSchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
  departmentId: z.string().optional(),
  // Scope a report to one site or camp (Worksite): filters to employees with an
  // active assignment to that worksite. Surfaced in the report filter bar.
  worksiteId: z.string().optional(),
  employeeId: z.string().optional(),
  // Response format. Must be part of the schema: the validate middleware
  // overwrites req.query with the Zod-parsed object, and Zod strips unknown
  // keys — so an omitted `format` here silently dropped `format=excel`, making
  // every "Export Excel" button hand back JSON bytes in a .xlsx wrapper.
  format: z.enum(['json', 'excel', 'csv', 'pdf']).optional(),
  /**
   * Comma-separated column keys, so an export can carry the columns the reader
   * is actually looking at.
   *
   * The workspace lets someone pick a view and hide columns, and the export
   * ignored all of it — you looked at Hours & OT and downloaded every column in
   * a different order. That is how a person reconciles the wrong file at month
   * end, and the file is the artefact payroll works from.
   *
   * Unknown keys are ignored rather than rejected: an older client, or a link
   * someone kept, must still produce a valid file. Omitted means every column,
   * which is what every existing caller gets today.
   *
   * Same reason `format` is here: `validate` overwrites req.query with the
   * Zod-parsed object and Zod strips unknown keys, so a param absent from this
   * schema is silently dropped.
   */
  columns: z.string().optional(),
  /**
   * The "Workers / Staff / All" group cut from the report filter bar, matched
   * exactly against the first-class `Employee.workforceType` column by
   * `resolveReportScope` (utils/reportScope.ts).
   *
   * THIRD time this schema has lost a param to the same trap — see `format` and
   * `columns` above. `validate` overwrites req.query with the Zod-parsed object
   * and Zod strips unknown keys, so the selector rendered, persisted itself to
   * the URL, stamped itself into the export URL, and changed NOTHING on /late,
   * /overtime, /missed-punches, /worker-site and /manpower-trend (the routes
   * without `validate` — /leave-balance, /document-expiry, /headcount — worked,
   * which is what made it look random).
   *
   * The reason it is an S1 and not a papercut: an agency invoice reconciled
   * from a "Workers only" export silently contained office staff.
   *
   * Enum members mirror `enum WorkforceType` in
   * packages/db/prisma/schema.prisma — an unknown value here would 422 rather
   * than be silently ignored, which is the correct trade for a filter that
   * decides what a bill is made of.
   */
  workforceType: z.enum(['SITE_WORKER', 'OFFICE_STAFF']).optional(),
  /**
   * THE WORKFORCE GROUP — Workers · Staff · Remote by the app's one rule
   * (filters/workforceGroup.ts, owner D1), which is what `workforceType` above
   * now reads as: an older link's `workforceType=SITE_WORKER` is Workers, so a
   * remote site worker is no longer in a "Workers only" export. Reports had no
   * Remote until M5.
   */
  group: WorkforceGroupQuerySchema.optional(),
  /**
   * Exact match on `Employee.nationality`, resolved the same way as
   * `departmentId`/`worksiteId` by `resolveReportScope`. Declared here for the
   * SAME reason as `workforceType` two entries up — the trap has already cost
   * one filter its wiring once, so this one is not added without the schema
   * that would silently have dropped it.
   *
   * Free-text rather than an enum: nationality has no lookup table in this
   * product, so there is nothing to enumerate against.
   */
  nationality: z.string().optional(),
  /**
   * Position / trade — exact match on `Employee.designationId`, applied by
   * `resolveReportScope` beside the four filters above.
   *
   * THE FOURTH LOSS TO THE SAME TRAP, and the first caught by a person rather
   * than a test: the owner filtered the workspace to one position, saw the chip
   * appear, and read a table still full of every other trade. Undeclared here,
   * `validate` stripped it and the server never saw it.
   * `reports.workforceType.route.test.ts` now crosses the middleware for it.
   * An unknown id stays a filter nothing matches, per `nationality` above.
   */
  designationId: z.string().optional(),
})
  // The bound belongs HERE and not in each of the eight handlers: a ninth report
  // endpoint gets it by writing `validate(ReportRangeSchema, 'query')`, which is
  // the line it was going to write anyway. `rangeBounds.test.ts` fails the build
  // if one is ever wired to an unrefined range schema instead.
  .superRefine(boundedDateRange(MAX_REPORT_RANGE_DAYS));
export type ReportRange = z.infer<typeof ReportRangeSchema>;
